import { AgendaItemVotesForm } from "@/components/agendaItems/AgendaItemVotesForm";
import { ApprovalButtons } from "@/components/common/ApprovalButtons";
import { PersonSelector } from "@/components/common/PersonSelector";
import { cn } from "@repo/shared/utils";
import type {
  AgendaItem,
  AgendaItemMotion,
  AgendaItemPosition,
  Document,
  Meeting,
  MotionType,
  Team,
  User,
} from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { ChevronDownIcon, ChevronUpIcon, XIcon } from "lucide-react";
import { useCallback, useId, useState } from "react";
import {
  createAgendaItemFromTemplate,
  getStringFromPosition,
  parsePositionFromString,
} from "./meetingRun/utilities";
import { CollapsiblePanel } from "./meetingRun/CollapsiblePanel";
import { InputInfo } from "@repo/ui/components/Base/InputInfo";
import { MutationSystem } from "@/lib/queuedMutations";
import { useUserStore } from "@/stores/userStore";
import { Label } from "@repo/ui/components/Base/Label";
import { DocumentSortableList } from "@/components/dnd";
import { SearchableDocumentsTable } from "@/components/common/SearchableTables";
import { useDocumentList } from "@/hooks/queryHooks/documentHooks";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";

export interface MotionRendererContext {
  motionText: string;
  meeting: Meeting;
  mutationSystem: MutationSystem;
  agendaItem?: AgendaItem;
}

export const MotionListRenderer = ({
  motionTypes,
  motions,
  darkShade = false,
  onChange,
  onMotionChange,
  onMotionDeferred,
  onLeave,
  votingUsers,
  tenantId,
  team,
  absentVoters,
  context,
}: {
  motionTypes?: Record<string, MotionType>;
  motions: AgendaItemMotion[];
  darkShade?: boolean;
  onChange?: () => void;
  onMotionChange?: (amended: string) => void;
  onMotionDeferred?: () => void;
  onLeave?: () => void;
  votingUsers: User[];
  tenantId: string;
  team: Team;
  absentVoters?: User[];
  context: MotionRendererContext;
}) => {
  if (!motionTypes) return null;

  return (
    <div className="flex flex-col gap-y-4 my-4">
      {motions.map((motion, index) => (
        <MotionRenderer
          key={index}
          motionTypes={motionTypes}
          motion={motion}
          darkShade={darkShade}
          onChange={onChange}
          onMotionChange={onMotionChange}
          onMotionDeferred={onMotionDeferred}
          onLeave={onLeave}
          onRemove={() => {
            // when a motion is removed, replace it with its children
            motions.splice(index, 1, ...(motion.nesting ?? []));
            onChange?.();
          }}
          votingUsers={votingUsers}
          tenantId={tenantId}
          team={team}
          absentVoters={absentVoters}
          context={context}
        />
      ))}
    </div>
  );
};

export interface ItemPositionOption extends AgendaItemPosition {
  label: string;
}

interface FieldValidationResult {
  valid: boolean;
  message: string;
}

interface TemplateValidationResult {
  name?: FieldValidationResult;
  description?: FieldValidationResult;
  position?: FieldValidationResult;
}

export const MotionRenderer = ({
  motionTypes,
  motion,
  darkShade = false,
  onChange,
  onMotionChange,
  onMotionDeferred,
  onLeave,
  onRemove,
  votingUsers,
  team,
  tenantId,
  absentVoters = [],
  context,
}: {
  motionTypes: Record<string, MotionType>;
  motion: AgendaItemMotion;
  darkShade?: boolean;
  onChange?: () => void;
  onMotionChange?: (amended: string) => void;
  onMotionDeferred?: () => void;
  onLeave?: () => void;
  onRemove?: () => void;
  votingUsers: User[];
  tenantId: string;
  team: Team;
  absentVoters?: User[];
  context: MotionRendererContext;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [originalMotionText] = useState(context.motionText);
  const inputId = useId();

  const motionType = motionTypes[motion.motionId] ?? {
    id: motion.motionId,
    name: "Unknown Motion",
    description:
      "(This type of motion may have existed previously, but is now no longer available.)",
    type: "subsidiary",
    amend: false,
    debate: false,
    interrupt: false,
    second: false,
    voting: false,
  };

  if (motionType.amend) {
    motion.amendment ??= context.motionText;
  }

  const updateMotionResolved = () => {
    motion.resolved =
      motion.seconded === "none" ||
      motion.status === "approved" ||
      motion.status === "rejected";

    motion.timestampEnd = motion.resolved ? Date.now() : undefined;

    if (motion.resolved && motionType.defers) {
      if (motion.status === "approved") {
        onMotionDeferred?.();
      }
    }

    if (motion.resolved && motionType.amend) {
      if (motion.status === "approved") {
        onMotionChange?.(motion.amendment ?? "");
      } else if (
        motion.status === "rejected" &&
        context.motionText === motion.amendment
      ) {
        onMotionChange?.(originalMotionText);
      }
    }
  };

  const approvalButtons = (
    <>
      <ApprovalButtons
        containerStyle="mt-0"
        showDeferred={false}
        value={motion.status ?? "pending"}
        onValueChange={(value) => {
          motion.status = value;
          updateMotionResolved();
          onChange?.();
        }}
      />
      <p className="mt-1.5">
        {motionType.voting === "majority"
          ? "Requires majority vote"
          : "Requires a two-thirds vote"}
      </p>
    </>
  );

  const [secondedOpen, setSecondedOpen] = useState(false);
  const [newItemValidationResult, setNewItemValidationResult] =
    useState<TemplateValidationResult>({});

  const getPositionValidation = useCallback(
    (position: AgendaItemPosition | undefined): FieldValidationResult => {
      // ensure position is valid within the meeting agenda
      const modules = context.meeting.agenda?.agendaModules ?? [];
      for (const module of modules) {
        if (
          position &&
          position.positionType === "atSectionStart" &&
          position.positionId === module.id
        ) {
          return { valid: true, message: "" };
        }
        for (const item of module.agendaItems ?? []) {
          if (
            position &&
            position.positionType === "afterItem" &&
            position.positionId === item.id
          ) {
            return { valid: true, message: "" };
          }
        }
      }
      return { valid: false, message: "Invalid position in agenda" };
    },
    [context.meeting]
  );

  const getNameValidation = useCallback(
    (name: string | undefined): FieldValidationResult => {
      if (!name || name.trim().length === 0) {
        return { valid: false, message: "Name cannot be empty" };
      }
      return { valid: true, message: "" };
    },
    []
  );

  const getDescriptionValidation = useCallback(
    (_: string | undefined): FieldValidationResult => {
      return { valid: true, message: "" };
    },
    []
  );

  const validateNewAgendaItem = useCallback(() => {
    const result: TemplateValidationResult = {};
    const template = motion.agendaItemTemplate;
    result.position = getPositionValidation(template?.position);
    result.name = getNameValidation(template?.partialAgendaItem?.name);
    result.description = getDescriptionValidation(
      template?.partialAgendaItem?.description
    );
    setNewItemValidationResult(result);
    return result;
  }, [
    motion.agendaItemTemplate,
    getPositionValidation,
    getNameValidation,
    getDescriptionValidation,
  ]);

  const validateName = useCallback((): boolean => {
    const result = getNameValidation(
      motion.agendaItemTemplate?.partialAgendaItem?.name
    );
    setNewItemValidationResult((prev) => ({
      ...prev,
      name: result,
    }));
    return result.valid;
  }, [motion.agendaItemTemplate, getNameValidation]);

  const validatePosition = useCallback((): boolean => {
    const position = motion.agendaItemTemplate?.position;
    const result = getPositionValidation(position);
    setNewItemValidationResult((prev) => ({
      ...prev,
      position: result,
    }));
    return result.valid;
  }, [motion.agendaItemTemplate, getPositionValidation]);

  const isNewItemValid = useCallback((): boolean => {
    const result = newItemValidationResult;
    return (
      (result.name ? result.name.valid : true) &&
      (result.description ? result.description.valid : true) &&
      (result.position ? result.position.valid : true)
    );
  }, [newItemValidationResult]);

  const addDocument = (document: Document) => {
    const template = motion.agendaItemTemplate ?? {};
    const partial = template.partialAgendaItem ?? {};
    partial.documentIds = [...(partial.documentIds ?? []), document.id];
    partial.documents = [...(partial.documents ?? []), document];
    template.partialAgendaItem = partial;
    motion.agendaItemTemplate = template;
    onChange?.();
  };

  const user = useUserStore((s) => s.user);
  const activeTeamId = useActiveTeamIdOrOrg();

  const isFullOrg = activeTeamId === "org";

  const { data: documentsUnfiltered } = useDocumentList(
    tenantId,
    activeTeamId ?? ""
  );

  // if active team does not match item's team, filter documents to ones this user created
  const documents = isFullOrg
    ? documentsUnfiltered?.filter((doc) => doc.createdBy === user?.id)
    : documentsUnfiltered;

  const getPositionOptions = (): ItemPositionOption[] => {
    const modules = context.meeting.agenda?.agendaModules ?? [];
    const options: ItemPositionOption[] = [];
    modules.forEach((module) => {
      options.push({
        positionId: module.id,
        positionType: "atSectionStart",
        label: module.name,
      });
      (module.agendaItems ?? []).forEach((item) => {
        options.push({
          positionId: item.id,
          positionType: "afterItem",
          label: item.name,
        });
      });
    });
    return options;
  };

  const getLabelFromPositionOption = (option: ItemPositionOption): string => {
    if (option.positionType === "atSectionStart") {
      return `At start of "${option.label}"`;
    }
    if (option.positionType === "afterItem") {
      return `\tAfter "${option.label}"`;
    }
    return option.label;
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-y-2 rounded-lg",
        darkShade ? "bg-slate-300" : "bg-slate-200"
      )}
    >
      <div className="flex p-4 flex-col gap-y-2">
        {/* Motion title / delete */}
        <div className="flex flex-row justify-between">
          <h3 className="text-lg font-bold">{motionType.name}</h3>
          <Button variant="ghost" className="w-8 h-8 p-0" onClick={onRemove}>
            <XIcon />
          </Button>
        </div>

        {/* Mover / Seconder */}
        <div className="flex flex-row gap-x-8 items-center">
          <div className="flex flex-row gap-x-1 items-center">
            <strong>Moved by:</strong>{" "}
            <PersonSelector
              innerLabel="Who moved this motion?"
              includeNoOne
              disableSpecific={[
                "none",
                ...(motion.seconded ? [motion.seconded] : []),
                ...(absentVoters?.map((user) => user.id) ?? []),
              ]}
              users={votingUsers}
              currentUser={motion.moved}
              onPickUser={(userId) => {
                motion.moved = userId;
                if (motionType.second) setSecondedOpen(true);
                onChange?.();
              }}
              openInitially={!motion.moved}
            />
          </div>
          {motionType.second && (
            <div className="flex flex-row gap-x-1 items-center">
              <strong>Seconded by:</strong>{" "}
              <PersonSelector
                innerLabel="Who seconded this motion?"
                includeNoOne
                disableSpecific={[
                  ...(motion.moved ? [motion.moved] : []),
                  ...(absentVoters?.map((user) => user.id) ?? []),
                ]}
                users={votingUsers}
                currentUser={motion.seconded}
                onPickUser={(userId) => {
                  motion.seconded = userId;
                  updateMotionResolved();
                  onChange?.();
                }}
                buttonStyles="border-secondary"
                inactiveButtonStyles="text-secondary"
                activeButtonStyles="bg-secondary hover:bg-secondary/90"
                open={secondedOpen}
                setOpen={setSecondedOpen}
              />
            </div>
          )}
        </div>

        {/* Amendment */}
        {motionType.amend && (
          <Input
            id={"amendedMotionText-" + inputId}
            label="Amended motion:"
            value={motion.amendment}
            onChange={(evt) => {
              motion.amendment = evt.target.value;
              onChange?.();
            }}
          />
        )}

        {/* Comments */}
        {!!motionType.comment && (
          <Input
            id={"motionComment-" + inputId}
            label={motionType.comment}
            value={motion.comments ?? ""}
            onChange={(evt) => {
              motion.comments = evt.target.value;
              onChange?.();
            }}
          />
        )}
      </div>

      {motionType.createAgendaItem && (
        <>
          <CollapsiblePanel
            title="New agenda item"
            contentClassName="flex flex-col gap-4"
          >
            <SelectInput
              disabled={
                motion.agendaItemTemplate?.createdAgendaItemId !== undefined
              }
              label="*Position in the meeting"
              error={
                newItemValidationResult.position
                  ? !newItemValidationResult.position.valid
                  : false
              }
              errorMessage={
                newItemValidationResult.position?.message ?? undefined
              }
              value={
                motion.agendaItemTemplate?.position
                  ? getStringFromPosition(motion.agendaItemTemplate.position)
                  : context.agendaItem
                    ? getStringFromPosition({
                        positionId: context.agendaItem.id,
                        positionType: "afterItem",
                      })
                    : undefined
              }
              placeholder="Select position..."
              onValueChange={(value) => {
                const template = motion.agendaItemTemplate ?? {};
                const position = parsePositionFromString(value);
                template.position = position ?? undefined;
                motion.agendaItemTemplate = template;
                onChange?.();
                validatePosition();
              }}
              options={getPositionOptions().map((option) => (
                <SelectItem
                  key={getStringFromPosition(option)}
                  value={getStringFromPosition(option)}
                >
                  {option.positionType === "atSectionStart" ? (
                    <strong>{getLabelFromPositionOption(option)}</strong>
                  ) : (
                    getLabelFromPositionOption(option)
                  )}
                </SelectItem>
              ))}
            />
            <Input
              id={"newAgendaItemName-" + inputId}
              label="*Name of agenda item"
              disabled={
                motion.agendaItemTemplate?.createdAgendaItemId !== undefined
              }
              value={motion.agendaItemTemplate?.partialAgendaItem?.name ?? ""}
              onChange={(evt) => {
                const template = motion.agendaItemTemplate ?? {};
                const partial = template.partialAgendaItem ?? {};
                partial.name = evt.target.value;
                template.partialAgendaItem = partial;
                motion.agendaItemTemplate = template;
                onChange?.();
              }}
              required={true}
              onBlur={() => validateName()}
              error={
                newItemValidationResult.name
                  ? !newItemValidationResult.name.valid
                  : false
              }
              errorMessage={newItemValidationResult.name?.message ?? undefined}
            />
            <TextArea
              id="description"
              label="Description"
              className="h-30"
              disabled={
                motion.agendaItemTemplate?.createdAgendaItemId !== undefined
              }
              value={
                motion.agendaItemTemplate?.partialAgendaItem?.description ?? ""
              }
              onChange={(evt) => {
                const template = motion.agendaItemTemplate ?? {};
                const partial = template.partialAgendaItem ?? {};
                partial.description = evt.target.value;
                template.partialAgendaItem = partial;
                motion.agendaItemTemplate = template;
                onChange?.();
              }}
            />
          </CollapsiblePanel>
          <CollapsiblePanel
            title="Attachments"
            contentClassName="flex flex-col gap-4"
            defaultOpen={false}
          >
            {documents && documents.length > 0 && (
              <>
                <Label label="Attached Documents" />
                <DocumentSortableList
                  //disabled={viewOnly || isSubmitting}
                  items={
                    motion?.agendaItemTemplate?.partialAgendaItem?.documentIds
                      ? motion.agendaItemTemplate.partialAgendaItem.documentIds.flatMap(
                          (docId) => {
                            const found = documents.find((d) => d.id === docId);
                            return found ? [found] : [];
                          }
                        )
                      : []
                  }
                  onChange={(items) => {
                    const template = motion.agendaItemTemplate ?? {};
                    const partial = template.partialAgendaItem ?? {};
                    partial.documentIds = items.map((doc) => doc.id);
                    partial.documents = items;
                    template.partialAgendaItem = partial;
                    motion.agendaItemTemplate = template;
                    onChange?.();
                  }}
                  editableLabel="Edit Document"
                  deleteLabel="Remove Document"
                />
                <SearchableDocumentsTable
                  //disabled={isSubmitting}
                  data={documents.filter(
                    (doc) =>
                      !motion?.agendaItemTemplate?.partialAgendaItem?.documentIds?.includes(
                        doc.id
                      )
                  )}
                  newPartialDocument={
                    isFullOrg
                      ? {
                          teamId: team.id !== "org" ? team.id : undefined,
                          createdBy: user?.id ?? undefined,
                        }
                      : undefined
                  }
                  onSubmit={addDocument}
                  onAdd={addDocument}
                />
              </>
            )}
          </CollapsiblePanel>
        </>
      )}
      <div className="flex p-4 flex-col gap-y-2">
        {/* Nested motions */}
        {motion.nesting?.length ? (
          <MotionListRenderer
            motionTypes={motionTypes}
            motions={motion.nesting}
            darkShade={!darkShade}
            onChange={onChange}
            onMotionChange={onMotionChange}
            onMotionDeferred={onMotionDeferred}
            onLeave={onLeave}
            votingUsers={votingUsers}
            tenantId={tenantId}
            team={team}
            absentVoters={absentVoters}
            context={context}
          />
        ) : null}

        {/* Discussion / Debate */}
        {motionType.debate ? (
          <TextArea
            id={"discussionDebate-" + inputId}
            value={motion.debate}
            onChange={(evt) => {
              motion.debate = evt.target.value;
              onChange?.();
            }}
            placeholder="Discussion / Debate"
          />
        ) : (
          <p>Debate not permitted for this type of motion.</p>
        )}

        {/* Voting */}
        {motionType.voting && (
          <>
            <div
              className={cn(
                "flex flex-row gap-x-4 items-start mt-1",
                // if motion is not seconded, or if last nested motion was not resolved, fade
                (motion.seconded === "none" ||
                  (motion.nesting?.length &&
                    !motion.nesting[motion.nesting.length - 1].resolved)) &&
                  "opacity-30"
              )}
            >
              <Button
                variant="ghost"
                className="w-8 h-8 p-0"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Button>
              {expanded ? (
                <h3 className="text-md font-bold mt-1.5">Voting</h3>
              ) : (
                approvalButtons
              )}
            </div>
            {expanded && (
              <>
                <AgendaItemVotesForm
                  votes={motion.votes}
                  votingUsers={votingUsers}
                  absentUsers={absentVoters}
                  onVotesChange={(votes, source) => {
                    motion.votes = votes;
                    // automatically approve / reject based on majority or two-thirds vote
                    if (
                      motion.votes.users?.length ===
                      votingUsers.length - absentVoters.length
                    ) {
                      motion.status = (
                        motionType.voting === "majority"
                          ? motion.votes.yes >
                            motion.votes.no + motion.votes.abstain
                          : motionType.voting === "twothirds"
                            ? motion.votes.yes >=
                              (motion.votes.no + motion.votes.abstain) * 2
                            : false
                      )
                        ? "approved"
                        : "rejected";
                      updateMotionResolved();
                    } else if (source === "clearAll") {
                      motion.status = "pending";
                      updateMotionResolved();
                    }
                    onChange?.();
                  }}
                />
                {approvalButtons}
              </>
            )}
          </>
        )}

        {/* Adjourned button */}
        {motionType.adjourn && motion.status === "approved" && (
          <>
            <p className="font-bold text-tertiary text-center">
              Meeting adjourned!
            </p>
            <Button
              variant="tertiary"
              className="mx-auto my-2 w-36"
              onClick={onLeave}
            >
              &laquo; Leave
            </Button>
          </>
        )}
        {motionType.createAgendaItem && (
          <div className="flex flex-row">
            <div className="flex flex-col gap-2">
              <Button
                className="w-auto self-start"
                disabled={
                  !!motion.agendaItemTemplate?.createdAgendaItemId ||
                  motion.status !== "approved" ||
                  !isNewItemValid()
                }
                onClick={async () => {
                  validateNewAgendaItem();
                  if (isNewItemValid() && motion.agendaItemTemplate) {
                    const createdItem = await createAgendaItemFromTemplate({
                      meeting: context.meeting,
                      mutationSystem: context.mutationSystem,
                      teamId: team.id,
                      template: motion.agendaItemTemplate,
                      tenantId,
                    });
                    motion.agendaItemTemplate.createdAgendaItemId =
                      createdItem?.id;
                    onChange?.();
                  }
                }}
              >
                Create Agenda
              </Button>
              {!isNewItemValid() && (
                <InputInfo info={"Required fields are not filled in"} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
